export const IMAGE_MAX_BYTES = 20 * 1024 * 1024
export const IMAGE_MAX_PIXELS = 75_000_000
export type ImageStatus = 'idle' | 'uploading' | 'uploaded' | 'translating' | 'success' | 'fail'
export type ImagePromptGroup = 'scene' | 'fidelity' | 'repaint'
export interface ImagePromptOption { value: string; semantic: string; wordsKey: string; tags: string[] }
export type ImagePromptOptions = Record<ImagePromptGroup, ImagePromptOption[]>
export interface ImageBilling {
  imageTranBaseCost: number
  imageTranOCRCostWord: number
  imageTranMagnification: number
  imageTranCostWord: number
}
export const imageBillingKeys = ['imageTranBaseCost', 'imageTranOCRCostWord', 'imageTranMagnification', 'imageTranCostWord'] as const

export function readImageBilling(raw: unknown): ImageBilling | null {
  if (!raw || typeof raw !== 'object') return null
  const data = raw as Record<string, unknown>
  for (const key of imageBillingKeys) {
    const value = data[key]
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return null
    if (key !== 'imageTranMagnification' && (!Number.isInteger(value) || value > 2147483647)) return null
  }
  return Object.fromEntries(imageBillingKeys.map(key => [key, data[key]])) as unknown as ImageBilling
}

/** 与 Java BusinessTeamEntitlementPolicy 一致；未知/旧全球档不能落回独立基础版。 */
export function imageEntitlementKey(team: Record<string, any>): string | null {
  const level = Number(team.serviceLevel)
  if (level >= 5 && team.globalContractNo && team.globalPlanCode) {
    return ({ BASE: 'global_base', PROFESSIONAL: 'global_middle', ADVANCED: 'global_high' } as Record<string, string>)[team.globalPlanCode] || null
  }
  return ({ 1: 'base', 2: 'middle', 3: 'high', 4: 'customerize', 5: 'global', 6: 'global_base', 7: 'global_middle', 8: 'global_high' } as Record<number, string>)[level] || null
}

export function minimumImageCost(billing: ImageBilling): number {
  return billing.imageTranBaseCost + Math.ceil(billing.imageTranOCRCostWord * billing.imageTranMagnification) + billing.imageTranCostWord
}

const promptGroups: Record<ImagePromptGroup, { prefix: string; semantics: string[] }> = {
  scene: { prefix: 'image2_scene_type_option', semantics: ['general', 'exam', 'invoice', 'contract', 'technical', 'planning', 'notice_board', 'fashion', 'poster', 'comic'] },
  fidelity: { prefix: 'image2_fidelity_option', semantics: ['strict', 'readable', 'source_target_inline'] },
  repaint: { prefix: 'image2_render_strategy_option', semantics: ['auto', 'direct', 'whole', 'fusion', 'fusion_structured', 'high_resolution'] },
}
export function emptyImageOptions(): ImagePromptOptions { return { scene: [], fidelity: [], repaint: [] } }
export function parseImagePromptOptions(response: any): ImagePromptOptions {
  const result = emptyImageOptions()
  const roots = response?.list ?? response?.data?.list ?? response?.data
  if (String(response?.code) !== '1' || !Array.isArray(roots)) return result
  const nodes: any[] = []
  function walk(items: any[], depth = 0) {
    if (depth > 10) return
    for (const item of items) {
      if (!item || typeof item !== 'object') continue
      nodes.push(item)
      if (Array.isArray(item.children)) walk(item.children, depth + 1)
    }
  }
  walk(roots)
  for (const group of Object.keys(promptGroups) as ImagePromptGroup[]) {
    const { prefix, semantics } = promptGroups[group]
    for (const node of nodes) {
      const match = String(node.wordsKey || '').match(new RegExp(`^${prefix}(\\d+)$`))
      if (!match || !/^\d+$/.test(String(node.id)) || Number(node.id) <= 0) continue
      const semantic = semantics[Number(match[1]) - 1]
      if (!semantic || result[group].some(option => option.value === String(node.id))) continue
      let tags: unknown = node.tag
      if (typeof tags === 'string') {
        const rawTags = tags
        try { tags = JSON.parse(rawTags) } catch { tags = rawTags.split(/[,，]/) }
      }
      result[group].push({ value: String(node.id), semantic, wordsKey: node.wordsKey, tags: Array.isArray(tags) ? tags.filter(tag => typeof tag === 'string') : [] })
    }
  }
  return result
}

export function imageMime(filename: string): string | null {
  return ({ png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp' } as Record<string, string>)[filename.split('.').pop()?.toLowerCase() || ''] || null
}

/** 同时校验扩展名和文件签名，不能把 SVG/GIF 改名后当支持的图片上传。 */
export function validateImageBytes(filename: string, size: number, bytes: Uint8Array): string {
  const expected = imageMime(filename)
  if (!expected) throw new Error('image2_err_type')
  if (size <= 0 || size > IMAGE_MAX_BYTES) throw new Error('bizImage_err_size')
  const png = [137, 80, 78, 71, 13, 10, 26, 10].every((v, i) => bytes[i] === v)
  const jpeg = bytes[0] === 255 && bytes[1] === 216 && bytes[2] === 255
  const ascii = (offset: number, s: string) => [...s].every((v, i) => bytes[offset + i] === v.charCodeAt(0))
  const webp = ascii(0, 'RIFF') && ascii(8, 'WEBP')
  const actual = png ? 'image/png' : jpeg ? 'image/jpeg' : webp ? 'image/webp' : null
  if (expected !== actual) throw new Error('image2_err_type')
  return expected
}

export function validateImageDimensions(width: number, height: number): boolean {
  if (!Number.isInteger(width) || !Number.isInteger(height) || width <= 0 || height <= 0) throw new Error('bizImage_err_decode')
  if (width * height > IMAGE_MAX_PIXELS) throw new Error('bizImage_err_pixels')
  return width > 3840 || height > 2160
}

export function imageFileKey(name: string): string { return name.replace(/\.[^.]+$/, '') }

/** 文件名只接受本站接口返回的对象名，不允许 URL、目录或查询串重写资源来源。 */
export function enterpriseImageUrl(base: string, blobName: string, view = true, version?: number, downloadName?: string): string {
  if (!blobName || /[\\/\x00-\x1f?#]/.test(blobName) || blobName === '.' || blobName === '..') return ''
  try {
    const url = new URL(`${base.replace(/\/?$/, '/')}${view ? 'view/' : ''}${encodeURIComponent(blobName)}`)
    if (!['https:', 'http:'].includes(url.protocol)) return ''
    if (version) url.searchParams.set('timestamp', String(version))
    if (!view && downloadName) url.searchParams.set('originName', downloadName)
    return url.href
  } catch { return '' }
}

export function isEnterpriseImageRecord(record: any): boolean {
  return Number(record?.imageFlag) === 1 && Boolean(imageMime(String(record?.fileType ? `x.${record.fileType}` : record?.blobFileName || '')))
}

export function enterpriseImageRecordId(raw: unknown): number {
  if (!/^[1-9]\d*$/.test(String(raw)) || (typeof raw !== 'string' && typeof raw !== 'number')) return 0
  const value = Number(raw)
  return Number.isSafeInteger(value) ? value : 0
}

export function enterpriseImageRecordTarget(record: any) {
  const id = enterpriseImageRecordId(record?.id)
  return isEnterpriseImageRecord(record) && id ? { path: '/home/ImageResult', query: { operateRecordId: String(id) } } : null
}

export function canEditEnterpriseImage(record: any, user: any): boolean {
  return isEnterpriseImageRecord(record) && Number(record.operateStatus) === 1 && Number(record.status || 0) !== 1
    && Boolean(user?.id && user?.bizTeamId) && String(record.bizTeamId) === String(user.bizTeamId)
    && (String(record.userId) === String(user.id) || [1, 2].includes(Number(user.bizTeamMemberRole)))
}

/** 保留企业文件查看/下载规则；图片编辑不使用 FILE_EDIT 位。 */
export function canAccessEnterpriseImage(record: any, user: any, action: 'view' | 'download'): boolean {
  if (!isEnterpriseImageRecord(record) || Number(record.operateStatus) !== 1 || Number(record.status || 0) === 1
    || !user?.id || !user?.bizTeamId || String(record.bizTeamId) !== String(user.bizTeamId)) return false
  if (String(record.userId) === String(user.id) || Number(user.bizTeamMemberRole) === 1) return true
  if (action === 'view' && Number(user.bizTeamMemberRole) === 2) return true
  return (Number(user.bizMemberpermission || 0) & (action === 'view' ? 1 : 2)) !== 0
}

export function imagePollVerdict(raw: any, key: string): { state: 'pending' | 'success' | 'fail' | 'empty'; progress?: number } {
  if (String(raw?.code) !== '1') return { state: 'empty' }
  // 不取字典首项，避免串到无关文档/上一张图片任务。
  const item = raw?.data?.[key]
  if (!item) return { state: 'empty' }
  if (item.status === 'success') return { state: 'success' }
  if (['fail', 'notEnough', 'undo'].includes(item.status)) return { state: 'fail' }
  const progress = Number(item.translateRate)
  return { state: 'pending', progress: Number.isFinite(progress) ? Math.max(0, Math.min(99, progress)) : undefined }
}
